import type {
  CrowdTippingToViewItem,
  PayToViewItem,
  PostContentTypeEnumItem,
  PostMediaItem,
  SubscribeToViewItem,
} from '@ps/api/types';
import type { CommunityChannelMessagePostLink } from '@ps/models/lib/community-channel';

import type { PostCardData } from '../../../posts/post-card/PostChatMessageView';

/**
 * Default chat-card model from a community post-link snapshot — the fast,
 * always-available render while `useFetchPostById` is in flight (and the
 * fallback if it fails). Mirrors PopshopRN's `defaultPost`: it carries the gate
 * snapshot AND resolves `restricted` up front, so a locked post paints its
 * paywall at its FINAL height immediately instead of flashing un-gated and then
 * growing a lock block once the fetch resolves — which drifts the recycling
 * message list mid-scroll.
 *
 * `restricted` isn't on the wire: the owner sees everything (never locked); a
 * non-owner is locked only when the post actually carries an enabled gate.
 *
 * The link is model-typed (`@ps/models`); `PostCardData` is the API-typed shape
 * the shared post card consumes. The gate/media/postType blobs are structurally
 * identical across the two type systems, so they're bridged with single
 * boundary casts — not field-by-field reads.
 */
export function postCardDataFromMessageLink(
  link: CommunityChannelMessagePostLink,
  { isOwner }: { isOwner: boolean },
): PostCardData {
  const gated = Boolean(
    link.subscribeToView?.enabled ||
    link.payToView?.enabled ||
    link.crowdTippingToView?.enabled,
  );
  return {
    title: link.title,
    description: link.description,
    postType: link.postType as unknown as PostContentTypeEnumItem,
    medias: link.medias as unknown as PostMediaItem[] | undefined,
    imageCount: link.imageCount,
    videoCount: link.videoCount,
    restricted: !isOwner && gated,
    subscribeToView: link.subscribeToView as unknown as
      | SubscribeToViewItem
      | undefined,
    payToView: link.payToView as unknown as PayToViewItem | undefined,
    crowdTippingToView: link.crowdTippingToView as unknown as
      | CrowdTippingToViewItem
      | undefined,
  };
}
